import { layerDepth, type SortReach } from "daedalus-core";
import type { ChunkPlacement } from "daedalus-renderer-phaser";

/**
 * The placement of a layer's chunk: layers in the Players band (ADR-047, from the manifest, else the lowest prop
 * layer) share the band's depth; tile layers there sort per row, so fences and walls interleave with props and
 * characters, while terrain layers (resolved from a variant) are flat art and draw whole at the band's floor;
 * ground layers stack below.
 *
 * @param layerId Layer id.
 * @param depth The layer's index in the chunk.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param terrain Whether the layer was resolved from a terrain.
 * @param band The index of the Players band's first layer.
 * @param mapHeight Map height in tiles, for the per-row sort.
 * @returns The placement.
 */
export function chunkPlacement(layerId: string, depth: number, cx: number, cy: number, terrain: boolean, band: number, mapHeight: number): ChunkPlacement {
  if (depth >= band) {
    return terrain ? { layerId, cx, cy, depth: layerDepth(depth, band), band: true } : { layerId, cx, cy, depth: layerDepth(depth, band), band: true, rowSort: { mapHeight } };
  }
  return { layerId, cx, cy, depth, band: false };
}

/**
 * The depth a neighbour's ground layer draws at beside the owner: its own index moved so its last ground layer sits on the
 * owner's last, when the owner has as many ground layers or more; else its layers spread over the owner's ground depths
 * in their own order, never below 0, so nothing drawn under the world's ground covers them (an animated cell's half-depth
 * nudge may then reach over the neighbour's next ground layer). With no ground layer on the owner they stack below 0.
 *
 * @param depth The layer's index, under `band`.
 * @param band The neighbour's Players band.
 * @param ownerBand The owner's.
 * @returns The depth.
 */
export function besideGroundDepth(depth: number, band: number, ownerBand: number): number {
  if (ownerBand >= band) {
    return ownerBand - band + depth;
  }
  return ownerBand > 0 ? (depth * ownerBand) / band : depth - band;
}

/**
 * The placement of a neighbouring map's layer chunk drawn beside the owning map (ADR-147 decision 6): at its position in
 * the owner's chunk grid, its band layers on the owner's band in the same order (the owner's band layer for the
 * neighbour's first band layer), and its ground layers under the owner's band in their own order, so nothing of the
 * neighbour's ground draws over a character and the two maps' Players bands interleave by row. Row-sorted layers sort
 * over the owner's height and the reach beyond it; a map's band is where its own Players band starts, and the maps' bands
 * need not match.
 *
 * @param layerId Layer id.
 * @param depth The layer's index in the neighbour's chunk.
 * @param cx Chunk column in the owner's grid.
 * @param cy Chunk row in the owner's grid.
 * @param terrain Whether the layer was resolved from a terrain.
 * @param band The index of the neighbour's Players band's first layer.
 * @param ownerBand The index of the owner's.
 * @param ownerHeight The owner's height in tiles.
 * @param reach The rows beyond the owner the depth sort spans.
 * @returns The placement.
 */
export function besidePlacement(layerId: string, depth: number, cx: number, cy: number, terrain: boolean, band: number, ownerBand: number, ownerHeight: number, reach: SortReach): ChunkPlacement {
  if (depth >= band) {
    const placed = layerDepth(ownerBand + depth - band, ownerBand);
    return terrain ? { layerId, cx, cy, depth: placed, band: true } : { layerId, cx, cy, depth: placed, band: true, rowSort: { mapHeight: ownerHeight, reach } };
  }
  return { layerId, cx, cy, depth: besideGroundDepth(depth, band, ownerBand), band: false };
}
